import { useEffect, useState } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import { TIER_LIMITS, TIER_PRICING, TRIAL_DAYS } from '@vcard/shared';
import { useAppTranslation } from '../hooks/useAppTranslation.js';

interface PlanInfo {
  tier: string;
  monthlyPriceId: string | null;
  yearlyPriceId: string | null;
}

type Interval = 'yearly' | 'monthly';

export interface SeatState {
  starter: number;
  business: number;
  interval: 'month' | 'year' | null;
}

const CONTACT_EMAIL = (import.meta.env['VITE_CONTACT_EMAIL'] as string | undefined) ?? 'info@visitenkarl.de';

const euro = (cents: number) => `€${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

/**
 * Seats: every user holds a Starter or Business seat, both can be mixed. Prices are per user.
 * Without a subscription the button starts Stripe checkout, with one it changes the seats in place.
 */
export function PlansOverview({ seats, isTrial }: { seats: SeatState; isTrial: boolean }) {
  const { t } = useAppTranslation();
  const hasSubscription = seats.starter + seats.business > 0;
  const [plans, setPlans] = useState<Record<string, PlanInfo>>({});
  const [interval, setInterval] = useState<Interval>(seats.interval === 'month' ? 'monthly' : 'yearly');
  const [starter, setStarter] = useState(hasSubscription ? seats.starter : 1);
  const [business, setBusiness] = useState(hasSubscription ? seats.business : 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/billing/plans', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { plans?: PlanInfo[] } | null) => {
        if (d?.plans) setPlans(Object.fromEntries(d.plans.map((p) => [p.tier, p])));
      })
      .catch(() => undefined);
  }, []);

  const configured = Boolean(
    plans['starter']?.monthlyPriceId && plans['starter']?.yearlyPriceId && plans['business']?.monthlyPriceId && plans['business']?.yearlyPriceId
  );
  const unitStarter = interval === 'yearly' ? TIER_PRICING.starter.yearly : TIER_PRICING.starter.monthly;
  const unitBusiness = interval === 'yearly' ? TIER_PRICING.business.yearly : TIER_PRICING.business.monthly;
  const total = starter * (unitStarter ?? 0) + business * (unitBusiness ?? 0);
  const perUnit = interval === 'yearly' ? t('plans.perUserYear', 'Nutzer / Jahr') : t('plans.perUserMonth', 'Nutzer / Monat');
  const unchanged = hasSubscription && starter === seats.starter && business === seats.business;

  async function submit() {
    if (starter + business < 1) { setError(t('plans.noSeats', 'Wähle mindestens einen Nutzer.')); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          interval: interval === 'yearly' ? 'year' : 'month',
          starterSeats: starter,
          businessSeats: business,
          successUrl: `${window.location.origin}/billing?success=1`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { checkoutUrl?: string; updated?: boolean; error?: string };
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      else if (data.updated) window.location.assign('/billing?success=1');
      else setError(data.error ?? `HTTP ${res.status}`);
    } finally {
      setBusy(false);
    }
  }

  const toggleCls = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? 'bg-accent-600 text-white' : 'text-ink-muted hover:text-ink'} disabled:opacity-50`;

  const card = 'flex flex-col rounded-xl border border-line bg-panel p-4';
  const bullets = (items: string[]) => (
    <ul className="mt-3 space-y-1.5 text-sm text-ink-muted">
      {items.map((l) => (
        <li key={l} className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-success-500" aria-hidden="true" />
          <span>{l}</span>
        </li>
      ))}
    </ul>
  );

  const stepper = (label: string, value: number, set: (n: number) => void) => (
    <div className="mt-auto pt-4">
      <p className="mb-1.5 text-xs text-ink-muted">{label}</p>
      <div className="flex items-center gap-2">
        <button type="button" aria-label={t('plans.fewer', 'Weniger')} onClick={() => set(Math.max(0, value - 1))} disabled={value <= 0}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink hover:bg-raised disabled:opacity-40">
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          type="number" min={0} max={1000} value={value} aria-label={label}
          onChange={(e) => set(Math.min(1000, Math.max(0, parseInt(e.target.value, 10) || 0)))}
          className="h-9 w-16 rounded-lg border border-line bg-panel text-center text-sm text-ink tabular-nums"
        />
        <button type="button" aria-label={t('plans.more', 'Mehr')} onClick={() => set(Math.min(1000, value + 1))}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink hover:bg-raised">
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  const price = (cents: number | null) => (
    <p className="mt-1 text-2xl font-semibold tracking-tight text-ink tabular-nums">
      {cents === null ? t('plans.onRequest', 'Auf Anfrage') : euro(cents)}
      {cents !== null ? <span className="text-xs font-normal text-ink-muted"> {perUnit}</span> : null}
    </p>
  );

  return (
    <div id="plans" className="space-y-4 scroll-mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{t('plans.title', 'Pläne')}</h2>
          <p className="mt-0.5 text-xs text-ink-faint">{t('plans.mixHint', 'Jeder Nutzer hat einen Platz. Du kannst Starter und Business mischen, zum Beispiel Business fürs Team und Starter für den Admin.')}</p>
        </div>
        <div role="group" aria-label={t('plans.interval', 'Abrechnungszeitraum')} className="inline-flex rounded-full border border-line bg-panel p-0.5">
          <button type="button" disabled={hasSubscription} aria-pressed={interval === 'yearly'} onClick={() => setInterval('yearly')} className={toggleCls(interval === 'yearly')}>
            {t('plans.yearly', 'Jährlich')}
          </button>
          <button type="button" disabled={hasSubscription} aria-pressed={interval === 'monthly'} onClick={() => setInterval('monthly')} className={toggleCls(interval === 'monthly')}>
            {t('plans.monthly', 'Monatlich')}
          </button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className={`${card} ${isTrial ? 'border-accent-500' : ''}`}>
          <div className="flex items-center justify-between">
            <p className="text-base font-semibold text-ink">{t('plans.trialName', 'Testmonat')}</p>
            {isTrial && (
              <span className="rounded-full bg-accent-100 dark:bg-accent-500/20 px-2 py-0.5 text-[11px] font-medium text-accent-700 dark:text-accent-300">{t('plans.current', 'Dein Plan')}</span>
            )}
          </div>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-ink">{t('plans.free', 'Kostenlos')}</p>
          {bullets([
            t('plans.trialDays', '{{count}} Tage kostenlos testen', { count: TRIAL_DAYS }),
            t('plans.trialFeatures', 'Alle Starter-Funktionen'),
            t('plans.oneUser', '1 Nutzer'),
            t('plans.trialEnd', 'Danach zeigt die Karte den Hinweis „Unbezahlte Version“'),
          ])}
        </div>

        <div className={`${card} ${seats.starter > 0 ? 'border-accent-500' : ''}`}>
          <p className="text-base font-semibold text-ink">{TIER_PRICING.starter.label}</p>
          {price(unitStarter)}
          {interval === 'yearly' && TIER_PRICING.starter.monthly !== null && (
            <p className="mt-0.5 text-xs text-ink-faint">{t('plans.orMonthly', 'oder {{price}} pro Nutzer und Monat', { price: euro(TIER_PRICING.starter.monthly) })}</p>
          )}
          {bullets([
            t('plans.cardsUnlimited', 'Unbegrenzt digitale Karten'),
            t('plans.linkPages', '{{count}} Link-Seite(n)', { count: TIER_LIMITS.starter.linkPages }),
            t('plans.backup', 'Backup-Export und Audit-Log'),
          ])}
          {stepper(t('plans.starterSeats', 'Starter-Nutzer'), starter, setStarter)}
        </div>

        <div className={`${card} ${seats.business > 0 ? 'border-accent-500' : ''}`}>
          <p className="text-base font-semibold text-ink">{TIER_PRICING.business.label}</p>
          {price(unitBusiness)}
          {interval === 'yearly' && TIER_PRICING.business.monthly !== null && (
            <p className="mt-0.5 text-xs text-ink-faint">{t('plans.orMonthly', 'oder {{price}} pro Nutzer und Monat', { price: euro(TIER_PRICING.business.monthly) })}</p>
          )}
          {bullets([
            t('plans.everythingStarter', 'Alles aus Starter'),
            t('plans.analytics', 'Analytics bis 90 Tage'),
            t('plans.leads', 'Leads und Scan von Visitenkarten'),
            t('plans.tracking', 'Tracking-Links'),
            t('plans.departments', 'Abteilungen und Rollen'),
          ])}
          {stepper(t('plans.businessSeats', 'Business-Nutzer'), business, setBusiness)}
        </div>

        <div className={card}>
          <p className="text-base font-semibold text-ink">{TIER_PRICING.enterprise.label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-ink">{t('plans.onRequest', 'Auf Anfrage')}</p>
          {bullets([
            t('plans.everythingBusiness', 'Alles aus Business'),
            t('plans.analyticsAll', 'Analytics unbegrenzt'),
            t('plans.customDomain', 'Eigene Domain'),
            t('plans.whiteLabel', 'White-Label und SSO'),
          ])}
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Visitenkarl Enterprise')}`}
            className="mt-auto block rounded-full border border-line px-4 py-2 text-center text-sm font-medium text-ink hover:bg-raised transition-colors"
          >
            {t('plans.contact', 'Anfragen')}
          </a>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-line bg-panel px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-ink-muted">
            {[
              starter > 0 ? `${starter}× ${TIER_PRICING.starter.label}` : '',
              business > 0 ? `${business}× ${TIER_PRICING.business.label}` : '',
            ].filter(Boolean).join(' + ') || t('plans.noSeats', 'Wähle mindestens einen Nutzer.')}
          </p>
          <p className="text-xl font-semibold text-ink tabular-nums">
            {euro(total)} <span className="text-sm font-normal text-ink-muted">{interval === 'yearly' ? t('plans.perYear', '/ Jahr') : t('plans.perMonth', '/ Monat')}</span>
          </p>
          {hasSubscription && <p className="mt-0.5 text-xs text-ink-faint">{t('plans.intervalLocked', 'Der Abrechnungszeitraum lässt sich unter „Abo verwalten“ ändern.')}</p>}
        </div>
        {configured ? (
          <button
            type="button"
            disabled={busy || unchanged || starter + business < 1}
            onClick={submit}
            className="rounded-full bg-accent-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-accent-500 disabled:opacity-50 transition-colors"
          >
            {busy ? t('plans.loading', 'Lädt…') : hasSubscription ? t('plans.changeSeats', 'Plätze ändern') : t('plans.book', 'Jetzt buchen')}
          </button>
        ) : (
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Visitenkarl Buchung')}`}
            className="rounded-full border border-line px-6 py-2.5 text-center text-sm font-medium text-ink hover:bg-raised transition-colors"
          >
            {t('plans.contact', 'Anfragen')}
          </a>
        )}
      </div>
      <p className="text-xs text-ink-faint">{t('plans.vatNote', 'Preise pro Nutzer, inkl. MwSt. Jederzeit kündbar. Die Rechnung kommt automatisch per E-Mail.')}</p>
    </div>
  );
}
